import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCreateDistrictDM } from "@/api/districts/mutations";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getDrfFieldErrors } from "@/utils/errors";

type CreateDistrictDMDialogProps = {
	open: boolean;
	onClose: () => void;
};

export function CreateDistrictDMDialog({
	open,
	onClose,
}: CreateDistrictDMDialogProps) {
	const { t } = useTranslation();
	const [name, setName] = useState("");
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
	const create = useCreateDistrictDM();

	function handleClose() {
		setName("");
		setFieldErrors({});
		onClose();
	}

	function handleSubmit() {
		if (!name.trim()) return;
		setFieldErrors({});
		create.mutate(
			{ name: name.trim() },
			{
				onSuccess: () => {
					toast.success(t("districts.created"));
					handleClose();
				},
				onError: (error) => {
					setFieldErrors(getDrfFieldErrors(error));
					toast.error(t("districts.createError"));
				},
			},
		);
	}

	return (
		<Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Utwórz osiedle</DialogTitle>
					<DialogDescription>Podaj nazwę nowego osiedla</DialogDescription>
				</DialogHeader>

				<div className="flex flex-col gap-4 py-2">
					<div className="flex flex-col gap-1.5">
						<Label htmlFor="district-name">Nazwa osiedla</Label>
						<Input
							id="district-name"
							name="district-name"
							autoComplete="off"
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder="np. Osiedle Słoneczne"
							onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
							aria-invalid={!!fieldErrors.name}
							aria-describedby={
								fieldErrors.name ? "district-name-error" : undefined
							}
						/>
						{fieldErrors.name && (
							<p
								id="district-name-error"
								className="text-destructive text-sm"
								role="alert"
							>
								{fieldErrors.name}
							</p>
						)}
					</div>
				</div>

				<DialogFooter>
					<Button
						size="sm"
						variant="outline"
						onClick={handleClose}
						disabled={create.isPending}
					>
						Anuluj
					</Button>
					<Button
						onClick={handleSubmit}
						disabled={!name.trim() || create.isPending}
					>
						{create.isPending ? "Tworzenie…" : "Utwórz"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
